import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Link as LinkIcon, PencilLine, Users } from "lucide-react";
import { api } from "@/lib/api";
import { IMPORT_IDS } from "@/constants/testIds/meal";
import ValidateIngredientsModal from "@/components/ValidateIngredientsModal";
import ManualEntryModal from "@/components/ManualEntryModal";
import { toast } from "sonner";

export default function Import() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [portions, setPortions] = useState(4);
  const [loading, setLoading] = useState(false);
  const [validateData, setValidateData] = useState(null); // { recipe_name, ingredients }
  const [manualOpen, setManualOpen] = useState(false);

  const handleGenerate = async () => {
    if (!url.trim()) {
      toast.error("Ajoute une URL de recette 🥕");
      return;
    }
    setLoading(true);
    try {
      const res = await api.importRecipe(url.trim(), portions);
      if (res.success) {
        setValidateData({
          recipe_name: res.recipe_name || "Recette",
          ingredients: res.ingredients,
        });
      } else {
        toast.error(res.error || "Impossible de récupérer la recette");
        setManualOpen(true);
      }
    } catch (e) {
      toast.error("Erreur d'import. Utilise la saisie manuelle.");
      setManualOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateConfirm = async (recipeName, ingredients) => {
    try {
      await api.addRecipe(recipeName, ingredients);
      toast.success(`« ${recipeName} » ajoutée à la liste 🎉`);
      setValidateData(null);
      navigate("/list");
    } catch {
      toast.error("Erreur lors de l'ajout à la liste");
    }
  };

  return (
    <main className="min-h-screen w-full px-6 py-8 md:py-12">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <button
            data-testid={IMPORT_IDS.backBtn}
            onClick={() => navigate("/")}
            className="chibi-btn-ghost inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Accueil
          </button>
          <button
            onClick={() => navigate("/list")}
            className="chibi-btn-ghost inline-flex items-center gap-2"
          >
            <Users className="w-4 h-4" /> Ma liste
          </button>
        </div>

        {/* Title */}
        <div className="text-center mb-10 chibi-pop">
          <h1 className="font-display font-semibold text-4xl md:text-6xl tracking-tight">
            Ajoute une <span style={{ color: "#E07A5F" }}>recette</span>
          </h1>
          <p className="mt-3 font-soft text-[#4A3F35]/70">
            Colle l&apos;URL, choisis tes portions, on s&apos;occupe du reste 🐰
          </p>
        </div>

        {/* Import card */}
        <div className="chibi-card p-6 md:p-10">
          <label className="font-soft text-sm font-semibold uppercase tracking-wide text-[#4A3F35]/70 mb-3 block">
            <LinkIcon className="inline w-4 h-4 mr-1" /> URL de la recette
          </label>
          <input
            data-testid={IMPORT_IDS.urlInput}
            type="url"
            placeholder="https://www.marmiton.org/recettes/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="chibi-input mb-6"
          />

          <div className="flex flex-col md:flex-row gap-4 md:items-end">
            <div className="flex-1">
              <label className="font-soft text-sm font-semibold uppercase tracking-wide text-[#4A3F35]/70 mb-3 block">
                Portions
              </label>
              <select
                data-testid={IMPORT_IDS.portionsSelect}
                value={portions}
                onChange={(e) => setPortions(parseInt(e.target.value))}
                className="chibi-input"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n} portion{n > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </div>
            <button
              data-testid={IMPORT_IDS.generateBtn}
              onClick={handleGenerate}
              disabled={loading}
              className="chibi-btn-primary inline-flex items-center gap-2 justify-center"
            >
              <Sparkles className="w-5 h-5" />
              {loading ? "Extraction..." : "Générer"}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-[#E8E2D2] text-center">
            <p className="text-sm text-[#4A3F35]/60 mb-3">Pas d&apos;URL sous la main ?</p>
            <button
              data-testid={IMPORT_IDS.manualBtn}
              onClick={() => setManualOpen(true)}
              className="chibi-btn-ghost inline-flex items-center gap-2"
            >
              <PencilLine className="w-4 h-4" /> Saisie manuelle
            </button>
          </div>
        </div>
      </div>

      {validateData && (
        <ValidateIngredientsModal
          data={validateData}
          onCancel={() => setValidateData(null)}
          onConfirm={handleValidateConfirm}
        />
      )}
      {manualOpen && (
        <ManualEntryModal
          onClose={() => setManualOpen(false)}
          onDone={() => {
            setManualOpen(false);
            navigate("/list");
          }}
        />
      )}
    </main>
  );
}
